<script setup lang="ts">
/**
 * « Message de / Maman », en deux arcs au-dessus de l'autocollant du bravo.
 *
 * Le bravo vaut par celui qui l'envoie : la signature en petites capitales,
 * en haut de l'écran, se lisait comme une mention légale. Elle descend sur
 * l'autocollant, courbée comme un tampon, sur deux lignes : l'amorce en petit
 * sur l'arc du haut, le nom en dessous, en grand, sur un arc du même centre.
 * Le nom y prend le ton de l'autocollant : la police d'affichage, la couleur
 * de l'enfant, bordée de blanc, posée sur une tranche dure, les lettres qui
 * sautillent.
 *
 * L'amorce est sa propre clé (`cheer.messageFromLead`) : le nom ne la suit pas
 * dans toutes les langues (en polonais, « od » le déclinerait), elle doit donc
 * se tenir seule. Sans nom connu, une seule ligne, celle de l'amorce :
 * « Un message pour toi » (à petite taille, les lettres qui sautillent ne se
 * lisaient plus).
 */
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  /** « Maman », « Papa » ou le prénom (cf. lib/cheerSender) ; `null` sans rien. */
  label: string | null
  /** Le pastel de l'enfant : le nom et sa tranche. */
  color: string
  shade: string
}>()

const { t } = useI18n()
const uid = useId()
const leadPathId = `cheer-arc-lead-${uid}`
const namePathId = `cheer-arc-name-${uid}`

const lead = computed(() => (props.label ? t('cheer.messageFromLead') : t('cheer.fromParent')))
const name = computed(() => props.label ?? '')

// Deux arcs du même centre (160, 204). Celui du nom, plus court (~255
// unités) : le nom garde 38 tant qu'il tient, et rétrécit au-delà, pour qu'un
// long prénom ne déborde pas de la courbe.
const LEAD_SIZE = 17
const NAME_ARC_LENGTH = 245
const nameSize = computed(() =>
  Math.max(18, Math.min(38, NAME_ARC_LENGTH / (Array.from(name.value).length * 0.62))))

// Les lettres du nom sautillent : une sur deux monte, l'autre descend.
const letters = computed(() => Array.from(name.value).map((char, i) => ({
  char,
  dy: i === 0 ? -2 : (i % 2 ? 4 : -4),
})))
</script>

<template>
  <svg class="cheer-arc block w-[min(86vw,340px)] overflow-visible" viewBox="0 0 320 172" aria-hidden="true">
    <defs>
      <path :id="leadPathId" d="M 20 150 A 150 150 0 0 1 300 150" fill="none" />
      <path :id="namePathId" d="M 60 163 A 108 108 0 0 1 260 163" fill="none" />
    </defs>

    <!-- L'amorce : petite, à la couleur foncée de l'enfant, un bord blanc fin. -->
    <text class="cheer-arc-text" :font-size="LEAD_SIZE" :fill="shade" stroke="#fff" stroke-width="4"
      stroke-linejoin="round" paint-order="stroke">
      <!-- Seule (sans nom), elle descend sur l'arc du nom, contre l'autocollant. -->
      <textPath :href="`#${name ? leadPathId : namePathId}`" startOffset="50%" text-anchor="middle">{{ lead }}</textPath>
    </text>

    <!-- Le nom. D'abord sa tranche : la même ligne, plus sombre, un peu plus bas. -->
    <text v-if="name" class="cheer-arc-text" transform="translate(0 3)" :font-size="nameSize" :fill="shade" :stroke="shade"
      stroke-width="7" stroke-linejoin="round">
      <textPath :href="`#${namePathId}`" startOffset="50%" text-anchor="middle">
        <tspan v-for="(l, i) in letters" :key="i" :dy="l.dy">{{ l.char }}</tspan>
      </textPath>
    </text>
    <text v-if="name" class="cheer-arc-text" :font-size="nameSize" :fill="color" stroke="#fff" stroke-width="7"
      stroke-linejoin="round" paint-order="stroke">
      <textPath :href="`#${namePathId}`" startOffset="50%" text-anchor="middle">
        <tspan v-for="(l, i) in letters" :key="i" :dy="l.dy">{{ l.char }}</tspan>
      </textPath>
    </text>
  </svg>
</template>

<style scoped>
.cheer-arc-text {
  font-family: var(--font-display);
  font-weight: 800;
  white-space: pre;
}
.cheer-arc {
  animation: cheer-arc-in 460ms 120ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes cheer-arc-in {
  from { transform: translateY(10px) scale(0.85); opacity: 0; }
  to { transform: none; opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .cheer-arc { animation: none; }
}
</style>
